// @givmo/app-charity — the pre-claim funnel's shared copy.
//
// What a caller is told about the SHAPE of the funnel — the product's one name, the three steps
// between opening the door and receiving money, and the work-email guidance — lives here rather than
// in each screen, so the auth doors, the org-search idle state and the claim wizard's step cue are
// literally the same sentences. Before this module they disagreed: the portal named itself three
// different ways on the auth screens alone, and the 3-step path existed only as prose nobody had
// written down.
//
// SCOPE: only strings that actually RENDER. This module is not a copy warehouse — an unrendered
// constant here reads to the next session as the live authority for a screen it no longer governs,
// and the value copy that once lived here (headline, subhead, no-cost line, value points) is now
// authored where it renders: the brand panel in `features/auth/auth-shell.tsx`. If you add a string
// here, it is because two or more surfaces must say it identically, or because a copy rail below
// must police it.
//
// COPY RAILS `funnel-copy.test.ts` enforces over EVERY export (it walks the module namespace, so a
// constant added tomorrow is covered the moment it lands):
//   - claiming is never a CONDITION OF PAYMENT. Givmo Charitable Fund grants to a nonprofit whether
//     or not it has claimed; what claiming changes is the RAIL (direct deposit) and the VISIBILITY
//     (an exact statement behind each grant) — never WHETHER the money arrives;
//   - instant verification is never promised. Every sentence naming the instant path ("instant",
//     "right away", "immediately") names its condition since D-CP-92, a vetted organization, and
//     never calls it usual — the backend decides;
//   - no pricing promise nobody signed (never "free forever");
//   - no internal vocabulary (no "tier", no grant-pipeline words) and no personalized amounts.

/**
 * The product's ONE self-name. The auth surface used to introduce itself as "the charity portal"
 * (sign-in chooser), "your nonprofit's presence on Givmo" (brand panel) and nothing at all
 * (sign-up) — three answers on three consecutive screens. This is the name the rest of the app
 * already uses (the dashboard's welcome, the document title, the boot error).
 *
 * The unified auth door heads with bare verbs ("Sign in or create your account") and names nothing,
 * so on the auth surface this appears in the sign-in form's cross-link to the create form — the one
 * place a caller is told what they are joining.
 */
export const PRODUCT_NAME = 'Givmo for Charities' as const;

/** A step in the pre-claim path, as the caller experiences it. */
export interface FunnelStep {
  /** 1-based position. */
  readonly n: number;
  readonly title: string;
  readonly detail: string;
}

/**
 * The three steps between opening the door and receiving money — the SAME three on the org-search
 * screen's preview, the verification wait, and the claim wizard's step cue, so the funnel never
 * surprises anyone with a step they were not told about.
 *
 * Steps 1 and 2 are exported individually because a screen naming ITS step reads it directly
 * (`FUNNEL_STEP_FIND` on /verify-email + org search, `FUNNEL_STEP_CONFIRM` on the wizard) rather than
 * indexing the array by a computed position. Step 3 is named by no screen, so it is local — its
 * copy still ships inside {@link FUNNEL_STEPS} and is still rail-scanned through it.
 *
 * Step 3's instant clause is hedged twice on purpose: "some" (the backend is authoritative) and
 * "organizations we've already vetted" (the condition). It must never read as a promise — see
 * {@link WORK_EMAIL_GUIDANCE}.
 */
export const FUNNEL_STEP_FIND: FunnelStep = {
  n: 1,
  title: 'Find your organization',
  detail: "Search Givmo's nonprofit catalog by name or EIN.",
};

export const FUNNEL_STEP_CONFIRM: FunnelStep = {
  n: 2,
  title: "Confirm you're authorized",
  detail: 'Claiming is done by someone who can act for the organization.',
};

// Leads with the path most claimants take since D-CP-92 (CPA-WEB91): the instant one runs only for an
// organization a super-admin has vetted.
const FUNNEL_STEP_PAYMENTS: FunnelStep = {
  n: 3,
  title: 'Verify and set up payments',
  detail:
    "One document, checked by a reviewer, or right away for some organizations we've already vetted.",
};

export const FUNNEL_STEPS: readonly FunnelStep[] = [
  FUNNEL_STEP_FIND,
  FUNNEL_STEP_CONFIRM,
  FUNNEL_STEP_PAYMENTS,
];

export const FUNNEL_TOTAL_STEPS = FUNNEL_STEPS.length;

/**
 * The unified auth door's subtitle — operator copy, verbatim. It is the ONE place the door advises
 * anything, and it does the job in subtitle register rather than as a paragraph of advice: the fork
 * that decides instant-vs-document verification is chosen at the door (a provider account is an
 * email address too), so a caller who reads nothing else still knows which address to reach for.
 * Since the 2026-08-18 door unification it renders on BOTH routes (/login-home and /sign-up) — the
 * returning caller reads past it; the outreach caller it exists for cannot miss it.
 *
 * Jonathan's line, verbatim (D-CP-97, 2026-10-22; CPA-WEB92). It once ended "for fast
 * verification", which became untrue under D-CP-92: most claims are reviewed by a person, so the
 * line no longer makes any verification-speed claim. It lives here, not inline in the screen,
 * because this module is the choke point that polices the funnel's claims, and its rails scan it.
 */
export const SIGN_UP_SUBTITLE = 'Use your nonprofit email address.';

/**
 * THE work-email guidance line — the single most load-bearing sentence in the funnel, because the
 * email a caller types on the sign-up form is half of what decides, minutes later, whether they are
 * verified instantly or asked for a document. Nothing upstream told them that, so most callers reach
 * for whichever address autofills.
 *
 * It renders in ONE place, as the helper under the Nonprofit email field (/sign-up/email), and it is
 * the only guidance on that screen: field helper text is where a caller looks for advice about the
 * field they are filling. The door's own nudge is {@link SIGN_UP_SUBTITLE}, not this sentence —
 * this one is never restated there.
 *
 * Conditional by construction: since D-CP-92 the instant path needs the other half too, a
 * super-admin's vetting of the organization (CPA-WEB91), so the sentence names that condition and
 * says "can", never "usually". Never state it unconditionally, and never restate it in a caller's own
 * words — import it.
 */
export const WORK_EMAIL_GUIDANCE =
  "Use your organization's work email — if we've already vetted your organization, an address at " +
  'its website can verify you right away.';

/**
 * THE personal-email refusal — Jonathan's copy, verbatim (D-CP-63, CPA-WEB53). Product copy, not
 * legal copy.
 *
 * The guidance above NUDGES; this sentence REFUSES. A personal-email address (any domain on the
 * backend's `DOMAIN_BLACKLIST`, the same list the employer portal's availability check refuses) can
 * never proceed through the normal process: the sign-up form refuses to create the account, and the
 * claim wizard refuses a signed-in caller whose address is on the list (an account that predates the
 * block, or one made through the consumer app). Both doors render THIS string — never a paraphrase —
 * so the manual avenue it names (`support@givmocharitable.org`, the ONE support address every
 * user-facing surface gives out) reads identically wherever a person meets it. The list itself lives
 * on the backend and is consulted through its availability read (`features/auth/personal-email-block.ts`);
 * this app never carries a copy of it.
 */
export const PERSONAL_EMAIL_BLOCK_MESSAGE =
  'Please use your nonprofit email address instead of a personal one. If your nonprofit does not ' +
  'have email addresses, please contact support@givmocharitable.org and we can manually verify you.';
